import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { CrashReporter } from "./components/CrashReporter";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ScrollToTop } from "./components/shared";
import FloatingContactHub from "./components/FloatingContactHub";
import { EMSAnnouncementPopup } from "./components/EMSAnnouncementPopup";
import { useAnalytics } from "./hooks/useAnalytics";
import { Toaster } from "sonner";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ServicePage from "./pages/ServicePage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import PrivacyPage from "./pages/PrivacyPage";
import CoachingOnlinePage from "./pages/CoachingOnlinePage";
import TermsPage from "./pages/TermsPage";
import HorairesPage from "./pages/HorairesPage";
import NotFoundPage from "./pages/NotFoundPage";
import CalorieCalculatorPage from "./pages/CalorieCalculatorPage";
import ClientAuthPage from "./pages/ClientAuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import SuiviProPage from "./pages/SuiviProPage";
import TransformationPage from "./pages/TransformationPage";
import CheckoutRedirectPage from "./pages/CheckoutRedirectPage";
import OffresPage from "./pages/OffresPage";
import AppShowcasePage from "./pages/AppShowcasePage";
import VideoGalleryPage from "./pages/VideoGalleryPage";
import TarifsPage from "./pages/TarifsPage";
import ParcoursPage from "./pages/ParcoursPage";
import FunnelPage from "./pages/FunnelPage";
import AdminPage from "./pages/AdminPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PostulerCoachingPage from "./pages/PostulerCoachingPage";
import PrivacyPolicyFR from "./pages/legal/PrivacyPolicyFR";
import PrivacyPolicyEN from "./pages/legal/PrivacyPolicyEN";
import CookiePolicyFR from "./pages/legal/CookiePolicyFR";
import CookiePolicyEN from "./pages/legal/CookiePolicyEN";
import SharedRecipePage from "./pages/SharedRecipePage";
import SharedProgramPage from "./pages/SharedProgramPage";
import SharedExercisePage from "./pages/SharedExercisePage";
import RecipeGalleryPage from "./pages/RecipeGalleryPage";
import { ClientAuthProvider } from "./context/ClientAuthContext";
import { ConsentProvider } from "./context/ConsentContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ConsentBanner } from "./components/ConsentBanner";

function MainSiteLayout() {
  useAnalytics(); // Track page views on route change
  const location = window.location;
  const isAppRoute = location.pathname === '/mon-espace' || location.pathname.startsWith('/recette-partagee') || location.pathname === '/recettes-communaute';
  const hideFooter = location.pathname === '/offres' || isAppRoute;

  return (
    <>
      {!isAppRoute && <Header />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:serviceId" element={<ServicePage />} />
        <Route path="/service/:serviceId" element={<ServicePage />} />
        <Route path="/tarifs" element={<TarifsPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/parcours/:slug" element={<ParcoursPage />} />
        <Route path="/postuler-coaching" element={<PostulerCoachingPage />} />
        <Route path="/coaching-online" element={<CoachingOnlinePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:articleId" element={<BlogArticlePage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/horaires" element={<HorairesPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {/* nLPD / RGPD : politiques bilingues */}
        <Route path="/politique-confidentialite" element={<PrivacyPolicyFR />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyEN />} />
        <Route path="/politique-cookies" element={<CookiePolicyFR />} />
        <Route path="/cookie-policy" element={<CookiePolicyEN />} />
        <Route path="/calculateur-calories" element={<CalorieCalculatorPage />} />
        <Route path="/calculateur-tarifs" element={<Navigate to="/offres" replace />} />
        <Route path="/espace-client" element={<ClientAuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/mon-espace" element={<ClientDashboardPage />} />
        <Route path="/recette-partagee/:token" element={<SharedRecipePage />} />
        <Route path="/programme-partage/:token" element={<SharedProgramPage />} />
        <Route path="/exercice-partage/:token" element={<SharedExercisePage />} />
        <Route path="/recettes-communaute" element={<RecipeGalleryPage />} />
        <Route path="/suivi-pro" element={<SuiviProPage />} />
        <Route path="/transformation" element={<TransformationPage />} />
        <Route path="/checkout/:option" element={<CheckoutRedirectPage />} />
        <Route path="/offres" element={<OffresPage />} />
        <Route path="/application" element={<AppShowcasePage />} />
        <Route path="/videos-exercices" element={<VideoGalleryPage />} />
        <Route path="/demarrer" element={<FunnelPage />} />

        {/* === Redirections vieilles URLs WordPress (SEO cleanup) === */}
        <Route path="/fr/2023/*" element={<Navigate to="/" replace />} />
        <Route path="/fr/2024/*" element={<Navigate to="/" replace />} />
        <Route path="/fr/2022/*" element={<Navigate to="/" replace />} />
        <Route path="/en/2023/*" element={<Navigate to="/" replace />} />
        <Route path="/en/2024/*" element={<Navigate to="/" replace />} />
        <Route path="/wp-content/*" element={<Navigate to="/" replace />} />
        <Route path="/wp-admin/*" element={<Navigate to="/" replace />} />
        <Route path="/wp-includes/*" element={<Navigate to="/" replace />} />
        <Route path="/fr/personal-training" element={<Navigate to="/services/personal-training" replace />} />
        <Route path="/fr/ems" element={<Navigate to="/services/ems-training" replace />} />
        <Route path="/fr/i-motion-ems" element={<Navigate to="/services/ems-training" replace />} />
        <Route path="/fr/styku" element={<Navigate to="/services/3d-scanner" replace />} />
        <Route path="/fr/power-plate" element={<Navigate to="/services/power-plate" replace />} />
        <Route path="/fr/hypoxico" element={<Navigate to="/services/hypoxico" replace />} />
        <Route path="/fr/boxe" element={<Navigate to="/services/boxing" replace />} />
        <Route path="/fr/nutrition" element={<Navigate to="/services/nutrition" replace />} />
        <Route path="/fr/plan-alimentaire" element={<Navigate to="/calculateur-calories" replace />} />
        <Route path="/fr/forfaits" element={<Navigate to="/tarifs" replace />} />
        <Route path="/fr/tarifs" element={<Navigate to="/tarifs" replace />} />
        <Route path="/fr/prix" element={<Navigate to="/tarifs" replace />} />
        <Route path="/fr/contact" element={<Navigate to="/contact" replace />} />
        <Route path="/fr/blog/*" element={<Navigate to="/blog" replace />} />
        <Route path="/fr/a-propos" element={<Navigate to="/about" replace />} />
        <Route path="/fr/qui-sommes-nous" element={<Navigate to="/about" replace />} />
        <Route path="/fr" element={<Navigate to="/" replace />} />
        <Route path="/en" element={<Navigate to="/" replace />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!hideFooter && <Footer />}
      {!isAppRoute && <FloatingContactHub />}
      {!isAppRoute && <EMSAnnouncementPopup />}
    </>
  );
}

function AppInner() {
  const { theme } = useTheme();
  return (
    <div className="App min-h-screen bg-black">
      <Toaster theme={theme} position="top-center" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/*" element={<MainSiteLayout />} />
        </Routes>
        <ConsentBanner />
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <CrashReporter>
      <ThemeProvider>
        <LanguageProvider>
          <ConsentProvider>
            <ClientAuthProvider>
              <AppInner />
            </ClientAuthProvider>
          </ConsentProvider>
        </LanguageProvider>
      </ThemeProvider>
    </CrashReporter>
  );
}

export default App;
